import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, HelpCircle, User } from "lucide-react";
import { useDarkMode } from "../DarkModeContext";
import { auth } from "../firebaseConfig";
import { loadProfileHybrid } from "../userDataSync";

export default function TabBar({ isAuthed }) {
  const { pathname } = useLocation();
  const { darkMode } = useDarkMode();
  const [hidden, setHidden] = useState(false);
  const [lastScroll, setLastScroll] = useState(0);
  const [avatarColor, setAvatarColor] = useState("blue-green");

  // --- Detect scroll direction ---
  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (Math.abs(currentScroll - lastScroll) < 10) return;
      if (currentScroll > lastScroll && currentScroll > 80) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      setLastScroll(currentScroll);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScroll]);

  // --- Load user's theme preference ---
  useEffect(() => {
    const loadUserTheme = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const profile = await loadProfileHybrid(user.uid, user.email);
          if (profile && profile.avatarColor) {
            setAvatarColor(profile.avatarColor);
          }
        } catch (err) {
          console.error("Error loading user theme:", err);
        }
      }
    };

    loadUserTheme();

    // Listen for profile updates
    window.addEventListener('profileUpdated', loadUserTheme);
    return () => window.removeEventListener('profileUpdated', loadUserTheme);
  }, []);

  // --- Color mapping - Brand colors ---
  const getThemeColors = (colorId) => {
    const colorMap = {
      "blue-green": { from: "#8FB3D3", to: "#385B37" },
      "sunset-orange": { from: "#F8662B", to: "#F8662B" },
      "earth-tone": { from: "#E4B4A6", to: "#413C04" },
      "forest-green": { from: "#385B37", to: "#385B37" },
      "sky-blue": { from: "#8FB3D3", to: "#8FB3D3" },
      "warm-peach": { from: "#E4B4A6", to: "#F8662B" },
    };
    return colorMap[colorId] || colorMap["blue-green"];
  };

  const themeColors = getThemeColors(avatarColor);

  // --- Tabs setup ---
  const tabs = [
    { to: "/", label: "Home", icon: Home },
    { to: "/how-to", label: "How To", icon: HelpCircle },
    { to: "/profile", label: "Profile", icon: User },
  ];

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
        ${hidden ? "opacity-0 translate-y-8 pointer-events-none" : "opacity-100 translate-y-0"}`}
      style={{
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        paddingLeft: '2.5%',
        paddingRight: '2.5%'
      }}
    >
      <div
        className="flex justify-around items-center w-full max-w-lg h-16 rounded-3xl backdrop-blur-2xl shadow-[0_6px_20px_rgba(0,0,0,0.08)] mx-auto"
        style={{
          backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          border: darkMode ? '1px solid rgb(55, 65, 81)' : '1px solid #E4B4A6'
        }}
      >
        {tabs.map(({ to, label, icon: Icon }) => {
          const active = pathname === to;

          return (
            <Link
              key={to}
              to={to}
              className="group relative flex flex-col items-center justify-center text-xs w-20"
            >
              {/* Active indicator */}
              {active && (
                <div
                  className="absolute -top-1 w-8 h-1 rounded-full"
                  style={{
                    background: `linear-gradient(to right, ${themeColors.from}, ${themeColors.to})`
                  }}
                />
              )}

              <div
                className={`p-2 rounded-xl transition-all duration-300`}
                style={active ? {
                  background: darkMode
                    ? `linear-gradient(to bottom right, ${themeColors.from}20, ${themeColors.to}20)`
                    : `linear-gradient(to bottom right, ${themeColors.from}10, ${themeColors.to}10)`
                } : {}}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = darkMode ? '#1F2937' : 'rgba(228, 180, 166, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = '';
                  }
                }}
              >
                <Icon
                  size={20}
                  style={active ? { color: themeColors.from } : {}}
                  className={`transition ${
                    active
                      ? ""
                      : darkMode
                        ? "text-gray-400 group-hover:text-gray-200"
                        : "text-gray-600 group-hover:text-gray-900"
                  }`}
                />
              </div>
              <span
                className={`mt-0.5 text-[11px] font-medium ${
                  active
                    ? ""
                    : darkMode
                      ? "text-gray-400 group-hover:text-gray-200"
                      : "text-gray-600 group-hover:text-gray-900"
                }`}
                style={active ? { color: themeColors.from } : {}}
              >
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
